
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  CommentCreateInterface,
  ProfileMapInterface,
  ReviewCommentCreateInterface,
} from '@/services/QC-V2/chat/interfaces';
import { CommentRate, CommentRepositoryInterface } from '@/services/QC-V2/comment/interfaces';
import { Roles } from '@/services/Roles';
import { TicketReviewFormDataInterface, TicketInterface } from '@/services/QC-V2/ticket/interfaces';
import DeleteCommentDialog from '@/components/dialogs/qc-v2/comment/DeleteCommentDialog.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    DeleteCommentDialog,
  },
  inject: [
    'profileRepository',
    'commentRepositoryV2',
  ],
})
export default class TicketComments extends Vue {
  @Prop() private ticket!: TicketInterface;
  @Prop() private reviewedId!: string;
  @Prop() private profilesMap!: ProfileMapInterface;
  @Prop({ default: false }) private disabled!: boolean;
  @Prop() private formData!: { [userId: string]: TicketReviewFormDataInterface };

  private reviewIndex: number = -1;

  private loading: boolean = true;

  private commentRepositoryV2!: CommentRepositoryInterface;

  private commentRates: typeof CommentRate = CommentRate;
  private commentRate: CommentRate = CommentRate.NOT_RATED;
  private editCommentRate: CommentRate = CommentRate.NOT_RATED;
  private ticketComment: string = '';

  private commentIndexForEdit: number = -1;
  private editCommentText: string = '';

  private userRoles: object = Roles;

  private deleteCommentDialogVisible: boolean = false;
  private deleteCommentDialogEvent!: string;
  private deleteCommentDialogData!: { ticketId?: number, commentId?: number, index?: number };

  private async created(): Promise<void> {
    await this.getProfiles();
    this.searchReviewIndex();
    this.loading = false;
  }

  @Watch('ticket')
  private async reloadData(): Promise<void> {
    this.loading = true;
    this.searchReviewIndex();
    await this.getProfiles();
    this.loading = false;
  }

  @Watch('reviewedId')
  private searchReviewIndex(): void {
    this.reviewIndex = this.ticket.reviews
      .findIndex((review) => !review.deletedAt && review.reviewedId === this.reviewedId);
  }

  private userHasScopes(scope: string): boolean {
    return this.$store.getters.scopes.includes(scope);
  }

  private async createTicketComment(): Promise<void> {
    this.ticketComment = this.ticketComment.trim();
    if (!this.ticketComment) {
      return;
    }

    if (this.reviewedId !== 'ticket') {
      this.createReviewComment();
      return;
    }

    const commentData: CommentCreateInterface = {
      author: this.$store.getters.id,
      text: this.ticketComment,
      rate: this.commentRate,
      timestamp: new Date().toISOString(),
    };

    try {
      const comment = await this.commentRepositoryV2.createTicketComment(this.ticket.id, commentData);
      this.$successDialog.show('Comment Created Successfully!');
      this.ticket.comments.push(comment);
      this.ticketComment = '';
      this.commentRate = CommentRate.NOT_RATED;
      this.editCommentRate = CommentRate.NOT_RATED;
      this.$emit('review-comment-added');
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private createReviewComment(): void {
    try {
      const commentData: ReviewCommentCreateInterface = {
        author: this.$store.getters.id,
        text: this.ticketComment,
        rate: this.commentRate,
        timestamp: new Date().toISOString(),
        addToDossier: true,
      };

      this.formData[this.reviewedId].comments.push(commentData);
      this.$successDialog.show('Comment Created Successfully!');

      this.searchReviewIndex();

      this.ticketComment = '';
      this.commentRate = CommentRate.NOT_RATED;
      this.editCommentRate = CommentRate.NOT_RATED;
      this.$emit('review-comment-added');
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async getProfiles(): Promise<void> {
    this.$emit('comment-profiles', this.profilesMap);
  }

  private showDeleteTicketCommentDialog(ticketId: number, commentId: number): void {
    this.deleteCommentDialogData = { ticketId, commentId };
    this.deleteCommentDialogEvent = 'ticket-comment-deleted';
    this.deleteCommentDialogVisible = true;
  }

  private async deleteComment(data: { ticketId: number, commentId: number }): Promise<void> {
    try {
      await this.commentRepositoryV2.deleteTicketComment(data.ticketId, data.commentId);
      this.$successDialog.show('Comment Deleted Successfully!');
      this.ticket.comments = this.ticket.comments.filter((comment) => comment.id !== data.commentId);
      this.deleteCommentDialogVisible = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private showEditBox(index: number, commentText: string): void {
    if (this.commentIndexForEdit === index) {
      this.editCommentText = '';
      this.commentIndexForEdit = -1;
      return;
    }

    this.editCommentText = commentText;
    this.commentIndexForEdit = index;
  }

  private async editComment(ticketId: number, commentId: number, commentIndex: number): Promise<void> {
    try {
      const commentData: ReviewCommentCreateInterface = {
        author: this.ticket.comments[commentIndex].author,
        text: this.editCommentText,
        rate: this.commentRate,
        timestamp: this.ticket.comments[commentIndex].timestamp,
      };

      await this.commentRepositoryV2.updateTicketComment(ticketId, commentId, commentData);
      this.$successDialog.show('Comment Edited Successfully!');
      this.ticket.comments[commentIndex].text = this.editCommentText;

      this.commentIndexForEdit = -1;
      this.editCommentText = '';
      this.commentRate = CommentRate.NOT_RATED;
      this.editCommentRate = CommentRate.NOT_RATED;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async editReviewComment(
    commentId: number,
    index: number,
  ): Promise<void> {
    try {

      const commentData = {
        author: this.$store.getters.id,
        text: this.editCommentText,
        rate: this.editCommentRate,
        addToDossier: this.formData[this.reviewedId].comments[index].addToDossier,
        timestamp: new Date().toISOString(),
      };

      if (commentId) {
        await this.commentRepositoryV2
          .updateChatReviewComment(this.ticket.reviews[this.reviewIndex].id, commentId, commentData);
      }

      this.formData[this.reviewedId].comments[index] = commentData;
      this.$successDialog.show('Comment Edited Successfully!');
      this.commentIndexForEdit = -1;
      this.editCommentText = '';
      this.commentRate = CommentRate.NOT_RATED;
      this.editCommentRate = CommentRate.NOT_RATED;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private showDeleteReviewCommentDialog(commentId: number, index: number): void {
    this.deleteCommentDialogData = { commentId, index };
    this.deleteCommentDialogEvent = 'review-comment-deleted';
    this.deleteCommentDialogVisible = true;
  }

  private async deleteReviewComment(data: { commentId: number, index: number }): Promise<void> {
    try {
      this.formData[this.reviewedId].comments.splice(data.index, 1);
      if (data.commentId) {
        await this.commentRepositoryV2.deleteChatReviewComment(
          this.ticket.reviews[this.reviewIndex].id,
          data.commentId,
        );
      }
      this.$successDialog.show('Comment Deleted Successfully!');
      this.$forceUpdate();
      this.deleteCommentDialogVisible = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private setCommentRate(rate: CommentRate): void {
    if (rate === this.commentRate) {
      this.commentRate = CommentRate.NOT_RATED;
      return;
    }

    this.commentRate = rate;
  }

  private setEditCommentRate(rate: CommentRate): void {
    if (rate === this.editCommentRate) {
      this.editCommentRate = CommentRate.NOT_RATED;
      return;
    }

    this.editCommentRate = rate;
  }

  private addReviewCommentToDossier(index: number): void {
    this.formData[this.reviewedId].comments[index].addToDossier = !this.formData[this.reviewedId].comments[index].addToDossier;
    this.$forceUpdate();
  }
}
